import type { AuditLogEntry } from './api'

/**
 * Turns the raw structured `AuditLogEntry` the backend returns into the
 * one-sentence, human-readable description an audit log viewer actually
 * needs ("who did what to whom") -- industry norm for this kind of screen
 * (GitHub/Okta/Slack audit logs all render a sentence, not a dotted action
 * string and a bag of ids). Backend detail is in
 * backend/app/core/permissions.py (`_write_audit_log`/`enrich_audit_log`)
 * and backend/app/routes/team.py.
 */

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  representative: 'Representative',
  // Legacy alias: audit rows written before the internal value was renamed
  // (migration e3b8c5d7a2f9) still carry 'rep' and must render the same.
  rep: 'Representative',
  analyst: 'Analyst',
}

const roleLabel = (role: unknown): string => (typeof role === 'string' && ROLE_LABELS[role]) || String(role ?? '')

/** actor_name > actor_email > a numbered placeholder. The placeholder
 * matters: actor_user_id has no ON DELETE SET NULL, so a since-deleted
 * user's older rows (written before this snapshot existed, or written by
 * a build that predates it) may have no actor_email/actor_name at all. */
export function auditActorLabel(entry: AuditLogEntry): string {
  const details = (entry.details ?? {}) as Record<string, unknown>
  return (
    (typeof details.actor_name === 'string' && details.actor_name) ||
    (typeof details.actor_email === 'string' && details.actor_email) ||
    `User #${entry.actor_user_id}`
  )
}

function str(details: Record<string, unknown>, key: string): string | undefined {
  const v = details[key]
  return typeof v === 'string' && v ? v : undefined
}

export function describeAuditEntry(entry: AuditLogEntry): string {
  const details = (entry.details ?? {}) as Record<string, unknown>
  const actor = auditActorLabel(entry)

  switch (entry.action) {
    case 'users.invite': {
      const email = str(details, 'invited_email')
      const role = str(details, 'invited_role')
      if (!email) return `${actor} invited someone`
      return `${actor} invited ${email}${role ? ` as ${roleLabel(role)}` : ''}`
    }
    case 'users.revoke_invite': {
      const email = str(details, 'invited_email')
      return email ? `${actor} revoked the invite to ${email}` : `${actor} revoked an invite`
    }
    case 'users.accept_invite': {
      const role = str(details, 'role')
      const inviter = str(details, 'invited_by_name') || str(details, 'invited_by_email')
      let sentence = `${actor} joined the team${role ? ` as ${roleLabel(role)}` : ''}`
      if (inviter) sentence += `, invited by ${inviter}`
      return sentence
    }
    case 'users.assign_role': {
      const target = str(details, 'target_name') || str(details, 'target_email')
      const oldRole = str(details, 'old_role')
      const newRole = str(details, 'new_role')
      if (!target || !oldRole || !newRole) return `${actor} changed a teammate's role`
      return `${actor} changed ${target}'s role from ${roleLabel(oldRole)} to ${roleLabel(newRole)}`
    }
    case 'users.remove': {
      const target = str(details, 'target_name') || str(details, 'target_email')
      const removedRole = str(details, 'removed_role')
      if (!target) return `${actor} removed a teammate from the team`
      return `${actor} removed ${target}${removedRole ? ` (${roleLabel(removedRole)})` : ''} from the team`
    }
    case 'integrations.manage': {
      // target_id is only ever populated when the route's own URL carries a
      // single identifying path param (e.g. a provider/service name) -- see
      // permissions.py's _write_audit_log heuristic. Most of the 25+ routes
      // sharing this one gate don't have one, so this stays a generic
      // sentence rather than promising specifics the backend doesn't send.
      return entry.target_id
        ? `${actor} updated the ${entry.target_id} integration`
        : `${actor} updated integration settings`
    }
    case 'campaigns.launch':
      return `${actor} launched a campaign`
    case 'contacts.bulk_export':
      return `${actor} exported contacts in bulk`
    case 'contacts.hard_delete':
      return `${actor} permanently deleted a contact`
    case 'workflows.activate': {
      // One action string covers three distinct events (request/approve/
      // reject) that share the same permission gate -- see workflows.py's
      // activate_workflow/approve_workflow_activation/
      // reject_workflow_activation. sub_action disambiguates which one.
      const workflowName = str(details, 'workflow_name') || 'a workflow'
      const requester = str(details, 'requested_by_name') || str(details, 'requested_by_email')
      switch (details.sub_action) {
        case 'requested':
          return `${actor} requested activation for ${workflowName}`
        case 'approved':
          return `${actor} approved activation for ${workflowName}${requester ? ` (requested by ${requester})` : ''}`
        case 'rejected':
          return `${actor} rejected activation for ${workflowName}${requester ? ` (requested by ${requester})` : ''}`
        default:
          return `${actor} activated ${workflowName}`
      }
    }
    default:
      // Never silently swallow an action we don't have a phrasing for yet --
      // the raw dotted string is still meaningful and better than hiding it.
      return `${actor} performed ${entry.action}`
  }
}
